// §11 Step 6 — Signature. Hand-rolled canvas pad (no dependency).
// Valid = 2+ strokes, or one stroke with ≥40 points, and drawn width ≥15% of pad.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import OnboardingLayout from './components/OnboardingLayout';
import StepGuard from './components/StepGuard';
import { useOnboarding } from '@/context/OnboardingContext';
import { OnboardingApiError, uploadSignature } from '@/api/onboarding';
import './onboarding.css';

interface Point {
  x: number;
  y: number;
}
type Stroke = Point[];

const PAD_HEIGHT = 220;

const SignatureCaptureForm = () => {
  const navigate = useNavigate();
  const { draft, setSignature } = useOnboarding();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const drawingRef = useRef(false);
  const [error, setError] = useState('');
  const [captured, setCaptured] = useState(!!draft.signaturePng);
  const [hasInk, setHasInk] = useState(false);
  const [saving, setSaving] = useState(false);

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = PAD_HEIGHT * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#061f42';
    }
    redraw();
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.offsetWidth, PAD_HEIGHT);
    for (const stroke of strokesRef.current) {
      if (stroke.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (const p of stroke) ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
  };

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e: React.PointerEvent): Point => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drawingRef.current = true;
    strokesRef.current.push([pos(e)]);
    setError('');
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    stroke.push(pos(e));
    setHasInk(true);
    redraw();
  };

  const onPointerUp = () => {
    drawingRef.current = false;
  };

  const handleUndo = () => {
    strokesRef.current.pop();
    setHasInk(strokesRef.current.length > 0);
    redraw();
  };

  const handleClear = () => {
    strokesRef.current = [];
    setHasInk(false);
    setError('');
    redraw();
  };

  const validate = (): boolean => {
    const strokes = strokesRef.current.filter((s) => s.length > 1);
    const enoughStrokes = strokes.length >= 2 || (strokes[0]?.length ?? 0) >= 40;
    const width = canvasRef.current?.offsetWidth ?? 1;
    const xs = strokes.flat().map((p) => p.x);
    const drawnWidth = xs.length ? Math.max(...xs) - Math.min(...xs) : 0;
    return enoughStrokes && drawnWidth >= width * 0.15;
  };

  const handleContinue = async () => {
    if (strokesRef.current.every((s) => s.length <= 1)) {
      setError('Please sign in the box above.');
      return;
    }
    if (!validate()) {
      setError('That looks too small to be a signature, please sign again.');
      return;
    }
    try {
      const png = canvasRef.current!.toDataURL('image/png');
      setSaving(true);
      setError('');
      // Send the specimen signature to the bank (step 6 endpoint).
      await uploadSignature(png);
      setSignature(png);
      setCaptured(true);
    } catch (err) {
      setError(
        err instanceof OnboardingApiError ? err.message : 'Could not save that signature, please sign again.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingLayout step={6} heading="Your signature" backTo="/open-account/identity-verification">
      {captured && draft.signaturePng ? (
        <div className="text-center py-4">
          <div className="nova-ob-card">
            <img src={draft.signaturePng} alt="Captured signature" style={{ maxWidth: '100%' }} />
          </div>
          <p className="text-success fw-semibold">Signature captured.</p>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-primary w-100"
              onClick={() => {
                setSignature(null);
                strokesRef.current = [];
                setHasInk(false);
                setCaptured(false);
              }}
            >
              Sign again
            </button>
            <button
              type="button"
              className="btn btn-primary w-100 nova-btn"
              onClick={() => navigate('/open-account/know-you')}
            >
              Continue
            </button>
          </div>
        </div>
      ) : (
        <>
          <p>Sign the way you sign on paper. This is kept on file as your specimen signature and is used to verify your instructions later.</p>

          <canvas
            ref={canvasRef}
            className="nova-ob-signpad"
            style={{ height: PAD_HEIGHT }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
          />

          <div className="d-flex gap-2 mt-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleUndo} disabled={!hasInk}>
              Undo
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClear} disabled={!hasInk}>
              Clear
            </button>
          </div>

          {error && <div className="invalid-feedback d-block mt-2">{error}</div>}

          <p className="form-text mt-2">
            Your signature is sent to the bank with your application and is not stored on this device.
          </p>

          <div className="nova-ob-footer">
            <div className="nova-ob-footer-inner">
              <button
                type="button"
                className="btn btn-primary btn-lg w-100 fw-semibold nova-btn"
                disabled={!hasInk || saving}
                onClick={handleContinue}
              >
                {saving && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
                Continue
              </button>
            </div>
          </div>
        </>
      )}
    </OnboardingLayout>
  );
};

const SignatureCapture = () => (
  <StepGuard requires={(d) => d.identityVerified}>
    <SignatureCaptureForm />
  </StepGuard>
);

export default SignatureCapture;
